import { useMemo, useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getTextColorOptions, isValidTextColor } from '@zaviago/storefront-config';

interface ColorPickerProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  label?: string;
  allowCustom?: boolean; // Allow hex input
  /** Disable the picker (Phase 4.2+ constraint support) */
  disabled?: boolean;
  /** Limit options to these presets (Phase 4.2+ constraint support) */
  presets?: string[];
}

/**
 * Text color picker with named colors from schema + optional custom hex input.
 * Uses schema-driven auto-discovery via getTextColorOptions().
 */
export function ColorPicker({
  value,
  onChange,
  label = 'Text Color',
  allowCustom = true,
  disabled = false,
  presets,
}: ColorPickerProps) {
  const allOptions = useMemo(() => getTextColorOptions(), []);

  // Filter to presets if provided
  const options = useMemo(() => {
    if (!presets || presets.length === 0) return allOptions;
    return allOptions.filter((opt: { id: string; value: string }) =>
      presets.includes(opt.id) || presets.includes(opt.value)
    );
  }, [allOptions, presets]);

  // Determine if current value is a preset or custom
  const isPreset = value && options.some((opt: any) => opt.id === value || opt.value === value);
  const presetOption = isPreset ? options.find((opt: any) => opt.id === value || opt.value === value) : null;
  const displayValue = presetOption ? presetOption.id : (value && !isPreset && value !== 'none' ? 'custom' : 'none');

  const [mode, setMode] = useState<'preset' | 'custom'>(
    displayValue === 'custom' ? 'custom' : 'preset'
  );
  const [customValue, setCustomValue] = useState(
    displayValue === 'custom' ? (value ?? '') : ''
  );

  // Sync mode and custom value when value prop changes
  useEffect(() => {
    const newIsPreset = value && options.some((opt: any) => opt.id === value || opt.value === value);
    const newPresetOption = newIsPreset ? options.find((opt: any) => opt.id === value || opt.value === value) : null;
    const newDisplayValue = newPresetOption ? newPresetOption.id : (value && !newIsPreset && value !== 'none' ? 'custom' : 'none');

    setMode(newDisplayValue === 'custom' ? 'custom' : 'preset');
    setCustomValue(newDisplayValue === 'custom' ? (value ?? '') : '');
  }, [value, options]);

  const handlePresetChange = (newValue: string) => {
    if (newValue === 'custom') {
      setMode('custom');
      return;
    }
    setMode('preset');
    onChange(newValue === 'none' ? undefined : newValue);
  };

  const handleCustomChange = (hex: string) => {
    setCustomValue(hex);
    if (isValidTextColor(hex)) {
      onChange(hex);
    } else if (hex === '') {
      onChange(undefined);
    }
  };

  const isCustomValid = !customValue || isValidTextColor(customValue);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Select
          value={displayValue}
          onValueChange={handlePresetChange}
          disabled={disabled}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Select color..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-muted-foreground">None (inherit)</span>
            </SelectItem>
            {options.map((opt: any) => (
              <SelectItem key={opt.id} value={opt.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-md border border-gray-300"
                    style={{ backgroundColor: opt.value }}
                  />
                  {opt.label}
                </div>
              </SelectItem>
            ))}
            {allowCustom && (
              <SelectItem value="custom">
                <span className="text-muted-foreground">Custom...</span>
              </SelectItem>
            )}
          </SelectContent>
        </Select>

        {mode === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={customValue.startsWith('#') ? customValue : '#000000'}
              onChange={(e) => handleCustomChange(e.target.value)}
              className="w-10 h-10 p-0 border rounded-md cursor-pointer"
            />
            <Input
              type="text"
              value={customValue}
              onChange={(e) => handleCustomChange(e.target.value)}
              placeholder="#000000"
              className={`w-28 ${!isCustomValid && customValue ? 'border-red-500' : ''}`}
            />
          </div>
        )}
      </div>
      {mode === 'custom' && !isCustomValid && customValue && (
        <p className="text-sm text-red-500">Invalid color format</p>
      )}
    </div>
  );
}
